import { useEffect, useState } from 'react';
import {
  ROLE_LABELS, roleRequiresBranch, roleRequiresHospital, roleRequiresServiceRegion,
  MAX_PAGE_SIZE, type Paginated, type UserRole,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface UserRow {
  id: string; email: string; name: string; role: UserRole;
  branchId: string | null; hospitalId: string | null; serviceRegionId: string | null;
  isActive: boolean; lastLoginAt: string | null;
}
interface Named { id: string; code: string; name: string }
interface Region extends Named { isProvince: boolean }

const EMPTY = {
  email: '', name: '', role: 'pmi_branch' as UserRole,
  branchId: '', hospitalId: '', serviceRegionId: '', password: '',
};

export function Users() {
  const { user: me, can } = useAuth();
  const list = usePagedList<UserRow>('/users');

  /**
   * Scope lookups, not a table: the whole short list is needed at once to fill the
   * dialog's selects and to label the Scope column, so these ask for the cap.
   */
  const [branches, setBranches] = useState<Named[]>([]);
  const [hospitals, setHospitals] = useState<Named[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Only an AiYO admin may create an AiYO admin — matching the server-side enforcement. */
  const roles: UserRole[] = (
    [
      'pmi_national', 'pmi_branch', 'hospital_orderer', 'hospital_finance',
      'service_admin_province', 'service_admin_city',
    ] as UserRole[]
  ).concat(me?.role === 'aiyo_admin' ? ['aiyo_admin'] : []);

  const needsBranch = roleRequiresBranch(form.role);
  const needsHospital = roleRequiresHospital(form.role);
  const needsRegion = roleRequiresServiceRegion(form.role);

  useEffect(() => {
    (async () => {
      const [b, h, r] = await Promise.all([
        api.get<Paginated<Named>>(`/branches?pageSize=${MAX_PAGE_SIZE}`).catch(() => null),
        api.get<Paginated<Named>>(`/hospitals?pageSize=${MAX_PAGE_SIZE}`).catch(() => null),
        api.get<Region[]>('/services/regions').catch(() => null),
      ]);
      if (b) setBranches(b.items);
      if (h) setHospitals(h.items);
      if (r) setRegions(r);
    })();
  }, []);

  function openForm() {
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.post('/users', {
        email: form.email, name: form.name, role: form.role, password: form.password,
        // Send only the relevant scope column — other roles must not be bound to one.
        branchId: needsBranch ? form.branchId : null,
        hospitalId: needsHospital ? form.hospitalId : null,
        serviceRegionId: needsRegion ? form.serviceRegionId : null,
      });
      setOpen(false);
      setNotice(`Pengguna ${form.email} dibuat.`);
      list.refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan pengguna.');
    } finally {
      setSaving(false);
    }
  }

  const nameOf = (of: Named[], id: string | null) =>
    id ? (of.find((x) => x.id === id)?.code ?? '—') : '—';

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Pengguna</h2>
        <p>Role menentukan izin sekaligus baris mana yang terlihat.</p>
      </div>

      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari nama atau email…"
        action={can('user:write') ? { label: 'Tambah pengguna', onClick: openForm } : undefined}
        empty="Belum ada pengguna."
        emptyFiltered="Tidak ada pengguna yang cocok dengan pencarian."
        columns={['Nama', 'Email', 'Role', 'Scope', 'Status']}
      >
        {list.items.map((r) => (
          <tr key={r.id}>
            <td>{r.name}</td>
            <td className="mono">{r.email}</td>
            <td><span className="pill">{ROLE_LABELS[r.role]}</span></td>
            <td className="mono muted">
              {r.branchId ? nameOf(branches, r.branchId)
                : r.hospitalId ? nameOf(hospitals, r.hospitalId)
                : r.serviceRegionId ? nameOf(regions, r.serviceRegionId)
                : 'lintas'}
            </td>
            <td>
              <span className={r.isActive ? 'pill accent' : 'pill'}>
                {r.isActive ? 'aktif' : 'nonaktif'}
              </span>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal
        open={open}
        title="Tambah pengguna"
        description="Role dan scope dipakai sebagai bagian dari query, bukan sebagai filter — mengubahnya mengubah data yang terlihat."
        onClose={() => setOpen(false)}
        onSubmit={create}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan pengguna'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}
          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="u-name">Nama</label>
              <input id="u-name" value={form.name} required
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="u-email">Email</label>
              <input id="u-email" type="email" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="u-role">Role</label>
              <select id="u-role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as UserRole, branchId: '', hospitalId: '', serviceRegionId: '' })}>
                {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="u-pass">Password awal</label>
              <input id="u-pass" type="password" value={form.password} required minLength={8}
                placeholder="minimal 8 karakter"
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>

            {needsBranch && (
              <div className="field">
                <label className="label" htmlFor="u-branch">Cabang</label>
                <select id="u-branch" value={form.branchId} required
                  onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
                  <option value="">— pilih cabang —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
                </select>
              </div>
            )}

            {needsRegion && (
              <div className="field">
                <label className="label" htmlFor="u-region">Wilayah</label>
                {/* Kota only: DKI Jakarta as a whole is the Admin Layanan Provinsi role. */}
                <select id="u-region" value={form.serviceRegionId} required
                  onChange={(e) => setForm({ ...form, serviceRegionId: e.target.value })}>
                  <option value="">— pilih wilayah —</option>
                  {regions.filter((x) => !x.isProvince).map((x) => (
                    <option key={x.id} value={x.id}>{x.code} · {x.name}</option>
                  ))}
                </select>
              </div>
            )}

            {needsHospital && (
              <div className="field">
                <label className="label" htmlFor="u-hosp">Rumah sakit</label>
                <select id="u-hosp" value={form.hospitalId} required
                  onChange={(e) => setForm({ ...form, hospitalId: e.target.value })}>
                  <option value="">— pilih rumah sakit —</option>
                  {hospitals.map((h) => <option key={h.id} value={h.id}>{h.code} · {h.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
